import { useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Hook para registrar automáticamente las visitas a un dashboard
 *
 * @param dashboardId - UUID del dashboard a trackear
 * @param enabled - Si el tracking está habilitado (default: true)
 *
 * @example
 * ```tsx
 * function DashboardPage({ dashboardId }) {
 *   useTrackDashboardView(dashboardId)
 *   return <div>Dashboard content...</div>
 * }
 * ```
 */
export function useTrackDashboardView(
  dashboardId: string | undefined,
  enabled: boolean = true
) {
  const hasTracked = useRef(false)

  useEffect(() => {
    // No hacer nada si no hay dashboardId o si está deshabilitado
    if (!dashboardId || !enabled || hasTracked.current) {
      return
    }

    // Marcar como trackeado para evitar duplicados
    hasTracked.current = true

    // Función para registrar la visita
    const trackView = async () => {
      try {
        // Obtener o crear session_id del localStorage
        let sessionId = localStorage.getItem('dashboard_session_id')
        if (!sessionId) {
          sessionId = generateSessionId()
          localStorage.setItem('dashboard_session_id', sessionId)
        }

        // Obtener información del usuario si está autenticado
        const { data: { user } } = await supabase.auth.getUser()

        // Obtener información del navegador
        const userAgent = navigator.userAgent
        const referrer = document.referrer || undefined

        // Registrar la visita usando la función RPC de Supabase
        // @ts-ignore - Supabase RPC types
        const { error } = await supabase.rpc('track_dashboard_view', {
          p_dashboard_id: dashboardId,
          p_session_id: sessionId,
          p_user_id: user?.id || undefined,
          p_user_agent: userAgent,
          p_referrer: referrer,
          // p_ip_address se puede obtener en el backend si es necesario
        })

        if (error) {
          console.error('Error tracking dashboard view:', error)
        } else {
          console.log('Dashboard view tracked successfully')
        }
      } catch (err) {
        console.error('Failed to track dashboard view:', err)
      }
    }

    // Ejecutar el tracking
    trackView()

    // Cleanup: resetear el flag cuando el componente se desmonte o cambie el dashboardId
    return () => {
      hasTracked.current = false
    }
  }, [dashboardId, enabled])
}

/**
 * Genera un ID de sesión único basado en timestamp y random
 */
function generateSessionId(): string {
  const timestamp = Date.now()
  const random = Math.random().toString(36).substring(2, 15)
  return `session_${timestamp}_${random}`
}

/**
 * Hook para obtener las estadísticas de un dashboard
 *
 * @param dashboardId - UUID del dashboard
 * @returns Objeto con las estadísticas y estado de carga
 *
 * @example
 * ```tsx
 * function DashboardStats({ dashboardId }) {
 *   const { stats, loading, error } = useDashboardStats(dashboardId)
 *
 *   if (loading) return <div>Cargando...</div>
 *   if (error) return <div>Error: {error}</div>
 *
 *   return (
 *     <div>
 *       <p>Total de visitas: {stats?.total_views}</p>
 *       <p>Visitas únicas: {stats?.unique_views}</p>
 *     </div>
 *   )
 * }
 * ```
 */
export interface DashboardStats {
  total_views: number
  unique_views: number
  views_last_7_days: number
  views_last_30_days: number
  avg_views_per_day: number
}

export function useDashboardStats(dashboardId: string | undefined) {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!dashboardId) {
      setLoading(false)
      return
    }

    const fetchStats = async () => {
      try {
        setLoading(true)
        setError(null)

        // @ts-ignore - Supabase RPC types
        const { data, error: rpcError } = await supabase.rpc('get_dashboard_stats', {
          dashboard_uuid: dashboardId,
        })

        if (rpcError) {
          throw rpcError
        }

        // La función RPC retorna un array con un solo elemento
        // @ts-ignore - Supabase RPC types
        if (data && data.length > 0) {
          setStats(data[0])
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err)
        setError(err instanceof Error ? err.message : 'Error desconocido')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [dashboardId])

  return { stats, loading, error }
}

// Necesitamos importar useState
import { useState } from 'react'

/**
 * Hook para obtener la tendencia diaria de visitas (últimos 30 días)
 *
 * @param dashboardId - UUID del dashboard
 * @returns Array con los datos de tendencia diaria
 *
 * @example
 * ```tsx
 * function DashboardTrend({ dashboardId }) {
 *   const { trend, loading } = useDashboardTrend(dashboardId)
 *
 *   return (
 *     <LineChart data={trend} />
 *   )
 * }
 * ```
 */
export interface DailyTrend {
  date: string
  views: number
  unique_views: number
}

export function useDashboardTrend(dashboardId: string | undefined) {
  const [trend, setTrend] = useState<DailyTrend[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!dashboardId) {
      setLoading(false)
      return
    }

    const fetchTrend = async () => {
      try {
        setLoading(true)
        setError(null)

        // @ts-ignore - Supabase RPC types
        const { data, error: rpcError } = await supabase.rpc('get_dashboard_daily_trend', {
          dashboard_uuid: dashboardId,
        })

        if (rpcError) {
          throw rpcError
        }

        setTrend(data || [])
      } catch (err) {
        console.error('Error fetching dashboard trend:', err)
        setError(err instanceof Error ? err.message : 'Error desconocido')
      } finally {
        setLoading(false)
      }
    }

    fetchTrend()
  }, [dashboardId])

  return { trend, loading, error }
}
